// ============================================================================
// FILE: src/modules/invoice/pages/InvoiceUploadPage.jsx
// UPDATED: 2025-12-04 (Fixed response mapping + detail fetch + UI state)
// PURPOSE: Upload invoice → OCR + LLM extraction → Show extracted info
// ============================================================================

import React, { useState } from "react";
import invoiceService from "../services/invoiceService";
import InvoicePreview from "../components/InvoicePreview";
import ExtractedFieldViewer from "../components/ExtractedFieldViewer";
import InvoiceLineItemTable from "../components/InvoiceLineItemTable";

const InvoiceUploadPage = () => {
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState(null);

  // --------------------------------------------------------------------------
  // File selector
  // --------------------------------------------------------------------------
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f);

    if (f) {
      setFilePreview(URL.createObjectURL(f));
    }
  };

  // --------------------------------------------------------------------------
  // Upload + Extract Flow
  // --------------------------------------------------------------------------
  const handleUpload = async () => {
    if (!file) {
      setError("Please select an invoice file first.");
      return;
    }

    setLoading(true);
    setError(null);
    setExtracted(null);

    try {
      // Tenant & Location are taken from JWT backend side
      const formData = new FormData();
      formData.append("file", file);

      // ---- 1️⃣ Upload Invoice
      const uploadRes = await invoiceService.uploadInvoice(formData);

      if (!uploadRes.success) {
        setError(uploadRes.message || "Upload failed.");
        return;
      }

      // backend returns `id`
      const invoiceId = uploadRes.id;
      if (!invoiceId) {
        setError("Upload succeeded but no invoice ID returned.");
        return;
      }

      // 🟢 Notify user upload ok
      alert("Invoice uploaded successfully!");

      // ---- 2️⃣ Get extracted details
      const detailRes = await invoiceService.getInvoiceDetail(invoiceId);

      if (detailRes.success && detailRes.data) {
        setExtracted(detailRes.data);
      } else {
        setError(detailRes.message || "Could not load extracted details.");
      }

    } catch (err) {
      console.error("Upload error:", err);
      setError("Network or server error occurred.");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // UI
  // --------------------------------------------------------------------------
  return (
    <div className="p-4">

      <h1 className="fw-bold fs-2 mb-4">Upload Invoice</h1>

      {/* ================= MAIN UPLOAD CARD ================= */}
      <div className="p-4 bg-white shadow rounded border">

        <div className="d-flex align-items-center gap-3">

          {/* File input */}
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            className="form-control"
            style={{ maxWidth: "300px" }}
          />

          {/* Gradient Upload Button */}
          <button
            onClick={handleUpload}
            disabled={loading}
            style={{
              background: loading
                ? "#a0a0a0"
                : "linear-gradient(90deg, #FF4DCF, #B800B8)",
              border: "none",
              color: "white",
              padding: "10px 20px",
              borderRadius: "8px",
              fontWeight: "600",
              boxShadow: "0px 3px 6px rgba(0,0,0,0.25)",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Processing..." : "Upload & Extract"}
          </button>

        </div>

        {/* Error Message */}
        {error && <p className="text-danger mt-3">{error}</p>}

        {/* Preview */}
        {filePreview && (
          <div className="mt-4">
            <InvoicePreview imageUrl={filePreview} />
          </div>
        )}

      </div>

      {/* ================= EXTRACTED DATA ================= */}
      {extracted && (
        <div className="mt-5 bg-white p-4 shadow rounded border">
          <h2 className="fw-bold fs-4 mb-3">Extracted Details</h2>

          <ExtractedFieldViewer data={extracted} />

          <h3 className="fw-semibold fs-5 mt-4 mb-2">Line Items</h3>
          <InvoiceLineItemTable items={extracted.items || []} />
        </div>
      )}

    </div>
  );
};

export default InvoiceUploadPage;
